/**
 * Admin → Reports
 *
 * Catalog of regulator-style reports the SystemAdmin can generate on demand.
 * Currently lists:
 *   - AAMVA NMVTIS Consumer Access Quarterly
 * New reports register here and in adminReportsService.ts.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import {
  generateNmvtisQuarterlyReport,
  NmvtisQuarterlyReportRequest,
} from '../../services/adminReportsService';

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan–Mar)' },
  { value: 2, label: 'Q2 (Apr–Jun)' },
  { value: 3, label: 'Q3 (Jul–Sep)' },
  { value: 4, label: 'Q4 (Oct–Dec)' },
] as const;

const Reports: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!isSystemAdmin) navigate('/dashboard');
  }, [user, authLoading, navigate, isSystemAdmin]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Default to most recent closed calendar-year quarter relative to today.
  const [defaultYear, defaultQ] = (() => {
    const now = new Date();
    const m = now.getUTCMonth() + 1; // 1-12
    const y = now.getUTCFullYear();
    // Current quarter
    let q = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4;
    // Default to the most recently *closed* quarter
    let prevQ = q - 1;
    let prevY = y;
    if (prevQ < 1) { prevQ = 4; prevY = y - 1; }
    return [prevY, prevQ];
  })();

  const [year, setYear] = useState<number>(defaultYear);
  const [quarter, setQuarter] = useState<number>(defaultQ);
  const [refundsCount, setRefundsCount] = useState<number>(0);
  const [refundsAverageAmount, setRefundsAverageAmount] = useState<number>(0);
  const [customNarrative, setCustomNarrative] = useState<string>('');
  const [useCustomWindow, setUseCustomWindow] = useState<boolean>(false);
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerate = async () => {
    setError('');
    setSuccess('');
    setGenerating(true);
    try {
      const req: NmvtisQuarterlyReportRequest = {
        year,
        quarter,
        refundsCount,
        refundsAverageAmount,
        customNarrative: customNarrative.trim() || null,
      };
      if (useCustomWindow) {
        req.customStartUtc = customStart ? new Date(customStart).toISOString() : null;
        req.customEndUtc = customEnd ? new Date(customEnd).toISOString() : null;
      }
      const { blob, filename } = await generateNmvtisQuarterlyReport(req);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess(`Generated ${filename}.`);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  if (authLoading || !user || !isSystemAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-[#c41e3a]" />
              Reports
            </h1>
            <p className="text-[#64748b] mt-1">Regulator-style submission packages. SystemAdmin only.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start gap-3"
                 style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 rounded-xl border flex items-center gap-3"
                 style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Report card */}
          <div className="glass-card overflow-hidden">
            <div className="px-6 py-4 border-b border-black/[0.06] flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-[#c41e3a]" />
              <div>
                <h2 className="text-lg font-bold text-[#0f172a]">AAMVA NMVTIS Consumer Access — Quarterly Submission</h2>
                <p className="text-sm text-[#64748b]">Builds Form 1 (Reseller) + Form 2 (Statistical) + data trace + submission notes. Returns a single zip.</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Year</label>
                  <input
                    type="number"
                    min={2024}
                    max={2099}
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value, 10) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Quarter</label>
                  <select
                    value={quarter}
                    onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  >
                    {QUARTERS.map(q => (
                      <option key={q.value} value={q.value}>{q.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#475569]">
                  <input
                    type="checkbox"
                    checked={useCustomWindow}
                    onChange={(e) => setUseCustomWindow(e.target.checked)}
                  />
                  Override activity window (for partial-quarter first submissions)
                </label>
                {useCustomWindow && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-xs text-[#64748b] mb-1">Start (UTC, inclusive)</label>
                      <input
                        type="datetime-local"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#64748b] mb-1">End (UTC, exclusive)</label>
                      <input
                        type="datetime-local"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Refunds — Number Issued</label>
                  <input
                    type="number"
                    min={0}
                    value={refundsCount}
                    onChange={(e) => setRefundsCount(parseInt(e.target.value, 10) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Refunds — Average $ Amount</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={refundsAverageAmount}
                    onChange={(e) => setRefundsAverageAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  Section D narrative override (leave blank to use the default narrative)
                </label>
                <textarea
                  rows={6}
                  value={customNarrative}
                  onChange={(e) => setCustomNarrative(e.target.value)}
                  placeholder="Default narrative covers: partial-quarter scope (when applicable), internal-use note, cache reconciliation, and 'no material business events'."
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30 font-mono text-sm"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#9a1729] hover:from-[#9a1729] hover:to-[#7a1421] disabled:opacity-50 text-white font-medium transition-all shadow-lg shadow-[#c41e3a]/25"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {generating ? 'Generating…' : 'Generate & Download'}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#94a3b8] mt-6">
            The generated zip contains the .xlsx workbook (Sheet 1: Reseller Submission, Sheet 2: Statistical Submission),
            a daily data-trace CSV, and a Markdown notes file describing methodology and submission destinations.
            Review everything before forwarding to AAMVA.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Reports;
